import { DialogRef } from "@angular/cdk/dialog";
import { Component, computed, effect, inject, input, resource, signal } from "@angular/core";
import { FormBuilder, FormControl, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatTimepickerModule } from "@angular/material/timepicker";
import { NgSelectModule } from "@ng-select/ng-select";
import { uniq } from "lodash-es";
import { firstValueFrom } from "rxjs";
import { Ticket } from "src/app/_models";
import { ModalService, ProjectService, TicketService, UserService } from "src/app/_services";
import type { BatchFormData } from "./batch-form-data.interface";

@Component({
  standalone: true,
  selector: "hawk-batch-edit-form",
  templateUrl: "./batch-edit-form.component.html",
  imports: [FormsModule, ReactiveFormsModule, NgSelectModule, MatDatepickerModule, MatTimepickerModule],
  host: {
    class: "flex flex-col gap-4 p-4",
  },
})
export class BatchEditFormComponent {
  public static open(modal: ModalService, options: { ticketIds: number[] }) {
    return modal.open<BatchEditFormComponent, Ticket[]>(BatchEditFormComponent, {
      inputs: options,
    });
  }

  private ticketService = inject(TicketService);
  private projectService = inject(ProjectService);
  private userService = inject(UserService);
  private fb = inject(FormBuilder);
  private dialogRef = inject(DialogRef);
  public ticketIds = input<number[]>([]);

  protected form = this.fb.group({
    ticketIds: this.fb.control<number[]>([]),
    status: this.fb.control<number>({ value: null, disabled: true }),
    priority: this.fb.control<number>({ value: null, disabled: true }),
    technician: this.fb.control<number[]>({ value: null, disabled: true }),
    description: this.fb.control<string>({ value: null, disabled: true }),
    operationDescription: this.fb.control<string>({ value: null, disabled: true }),
    operationDate: this.fb.control<string>({ value: null, disabled: true }),
    timeEstimation: this.fb.control<number>({ value: null, disabled: true }),
  });

  protected data = resource({
    params: this.ticketIds,
    loader: async ({ params }) => {
      const tickets = await this.loadTickets(params);

      const priorityOptions = await this.resolvePriorityOptions(tickets);
      const technicianOptions = await this.resolveTechnicianOptions(tickets);
      const statusOptions = await this.resolveStatusOptions(tickets);
      return {
        tickets,
        priorityOptions,
        technicianOptions,
        statusOptions,
      };
    },
  });
  protected isLoading = this.data.isLoading;
  protected tickets = computed(() => this.data.value()?.tickets || []);
  protected priorityOptions = computed(() => this.data.value()?.priorityOptions || []);
  protected technicianOptions = computed(() => this.data.value()?.technicianOptions || []);
  protected statusOptions = computed(() => this.data.value()?.statusOptions || []);
  protected batchError = signal<string | null>(null);
  protected isSaving = signal(false);

  public constructor() {
    effect(() => {
      this.form.controls.ticketIds.setValue(this.ticketIds());
    });
  }

  private loadTickets(ids: number[]) {
    this.form.controls.status.disable();
    return Promise.all(
      ids.map((id) => {
        return firstValueFrom(this.ticketService.getTicket(String(id)));
      }),
    );
  }

  private async resolveStatusOptions(tickets: Ticket[]) {
    const projectIds = uniq(tickets.map((ticket) => ticket.project_id));
    const projectOptions = await Promise.all(
      projectIds.map((id) => {
        return firstValueFrom(this.projectService.getStatuses(id));
      }),
    ).then((lists) => {
      return intersectionOf({ data: lists, equal: (a, b) => a.id === b.id });
    });
    return projectOptions;
  }

  private async resolvePriorityOptions(tickets: Ticket[]) {
    const projectIds = uniq(tickets.map((ticket) => ticket.project_id));
    const projectOptions = await Promise.all(
      projectIds.map((id) => {
        return firstValueFrom(this.projectService.getPriorities(id));
      }),
    ).then((lists) => {
      return intersectionOf({ data: lists, equal: (a, b) => a.id === b.id });
    });
    return projectOptions;
  }

  private async resolveTechnicianOptions(tickets: Ticket[]) {
    const projectIds = tickets.map((it) => it.project_id);
    const technicians = await Promise.all(
      projectIds.map((id) => {
        return firstValueFrom(this.userService.getProjectTechnicians(id));
      }),
    ).then((lists) => {
      return intersectionOf({ data: lists, equal: (a, b) => a.id === b.id });
    });
    return technicians;
  }

  protected setControlEnabled(control: FormControl, enabled: boolean) {
    if (enabled) {
      control.enable();
    } else {
      control.disable();
    }
  }

  protected handleCancel() {
    this.dialogRef.close();
  }

  protected async handleSave() {
    const data: BatchFormData = {
      ticketIds: this.ticketIds(),
    };
    if (this.form.controls.status.enabled) {
      data.status = this.form.controls.status.value;
    }
    if (this.form.controls.priority.enabled) {
      data.priority = this.form.controls.priority.value;
    }
    if (this.form.controls.technician.enabled) {
      data.technicians = this.form.controls.technician.value;
    }
    if (this.form.controls.description.enabled) {
      data.description = this.form.controls.description.value;
    }
    if (this.form.controls.operationDate.enabled) {
      data.operationDate = this.form.controls.operationDate.value;
    }
    if (this.form.controls.operationDescription.enabled) {
      data.operationDescription = this.form.controls.operationDescription.value;
    }
    if (this.form.controls.timeEstimation.enabled) {
      data.timeEstimation = this.form.controls.timeEstimation.value;
    }

    this.batchError.set(null);
    this.isSaving.set(true);
    await firstValueFrom(this.ticketService.updateTicketsBatch(data))
      .then(() => {
        this.dialogRef.close();
      })
      .catch((error) => {
        this.batchError.set("Speichern fehlgeschlagen: " + (error?.message || ""));
      })
      .finally(() => {
        this.isSaving.set(false);
      });
  }
}

function intersectionOf<T>({ data, equal }: { data: T[][]; equal: (a: T, b: T) => boolean }): T[] {
  if (data.length === 0) {
    return [];
  }
  return data.reduce((acc, arr) => {
    return acc.filter((item) => arr.some((a) => equal(a, item)));
  });
}
